import {
	faBuilding,
	faCheck,
	faEdit,
	faExclamationCircle,
	faExclamationTriangle,
	faImage,
	faKey,
	faPuzzlePiece,
	faStream,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { CustomEditFlowPayload, useFlowStore } from "@/store/flow.store";
import { isValidBase64 } from "@/utils/base64";
import { ApiError, handleApiError } from "@/utils/errorHandler";
import {
	CredentialSchemaDefinition,
	CredentialSchemaProperty
} from "./CreateFlowPanel";
import { EditFlowProps } from "./ViewFlow";
import { FlowTarget } from "./ViewFlowTraget";

const EditFlowPanel: React.FC<EditFlowProps> = ({
	flowTargetId,
	onUpdateFlow,
	onDeleteFlow,
	flows,
	isLoading = false
}) => {
	const { selectedFlowTypeId } = useFlowStore();
	const [credentials, setCredentials] = useState<Record<string, string>>({});
	const [credentialErrors, setCredentialErrors] = useState<
		Record<string, string>
	>({});
	const [credentialSchema, setCredentialSchema] =
		useState<CredentialSchemaDefinition | null>(null);
	const [logoImage, setLogoImage] = useState<string | null>(null);
	const [logoDeleted, setLogoDeleted] = useState<boolean>(false);
	const [editMode, setEditMode] = useState<boolean>(true); // Direct edit mode
	const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
	const [showCredentialWarning, setShowCredentialWarning] =
		useState<boolean>(false);
	const [showCredentialFields, setShowCredentialFields] =
		useState<boolean>(false);
	const [flowStatuses, setFlowStatuses] = useState<Record<string, boolean>>({});
	const [hasExistingCredentials, setHasExistingCredentials] =
		useState<boolean>(false);
	const hasCalledApi = useRef(false);
	const panelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (flows) {
			const initialStatuses = flows.reduce(
				(acc, flow) => {
					acc[flow.flowDefinitionId] = flow.flowStatus === "ENABLED";
					return acc;
				},
				{} as Record<string, boolean>
			);
			setFlowStatuses(initialStatuses);
		}
	}, [flows]);

	// Manage focus trap to prevent focused elements from being under aria-hidden
	useEffect(() => {
		// When panel mounts, set a tab index and focus it to manage focus containment
		if (panelRef.current) {
			panelRef.current.setAttribute("tabindex", "-1");
			panelRef.current.focus();
		}
	}, []);

	const getTargetByIdMutation = useMutation({
		mutationFn: async (targetId: string) => {
			if (!selectedFlowTypeId) {
				return {
					id: "",
					name: "",
					logo: "",
					status: "",
					credentialSchema: { type: "object" },
					inputSchema: {},
					flowTypeId: "",
					brandId: null,
					createdAt: "",
					updatedAt: "",
					createdBy: "",
					updatedBy: ""
				} as unknown as FlowTarget;
			}

			const { data } = await apiClient.get(
				resolveEndpoint(
					API_ENDPOINTS,
					["FLOW_TARGETS", "GET_BY_ID"],
					selectedFlowTypeId,
					targetId
				)
			);
			return data as FlowTarget;
		},
		onSuccess: (data: FlowTarget) => {
			if (data.credentialSchema) {
				setCredentialSchema(
					data.credentialSchema as unknown as CredentialSchemaDefinition
				);

				const initialCredentials: Record<string, string> = {};

				if (data.credentialSchema.properties) {
					Object.keys(data.credentialSchema.properties).forEach((key) => {
						initialCredentials[key] = "";
					});
				}
				setCredentials(initialCredentials);
				setHasExistingCredentials(flows && flows.length > 0);
			}

			if (data.logo) {
				setLogoImage(data.logo);
			} else {
				setLogoImage(null);
			}
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to fetch target details");
			toast.error(
				"Could not load target details. Make sure flow type is selected."
			);
		}
	});

	useEffect(() => {
		if (flowTargetId && !hasCalledApi.current) {
			hasCalledApi.current = true;
			getTargetByIdMutation.mutate(flowTargetId);
		}
	}, [flowTargetId, getTargetByIdMutation]);

	useEffect(() => {
		setHasExistingCredentials(flows && flows.length > 0);
	}, [flows]);

	const handleCredentialChange = (key: string, value: string) => {
		setCredentials((prev) => ({ ...prev, [key]: value }));
		validateCredentialValue(key, value);
	};

	const getInputType = (property: CredentialSchemaProperty): string => {
		const type = property.type || "string";

		if (type === "string") {
			return "text";
		}
		if (type === "number" || type === "integer") {
			return "number";
		}
		if (type === "boolean") {
			return "checkbox";
		}

		return "text";
	};

	const isFieldRequired = (fieldName: string): boolean => {
		return credentialSchema?.required?.includes(fieldName) ?? false;
	};

	const validateCredentialValue = (key: string, value: string) => {
		if (!credentialSchema?.properties) return;

		const property = credentialSchema.properties[key];

		let error = "";

		if (isFieldRequired(key) && (!value || value.trim().length < 1)) {
			error = "This field is required";
		} else if (value) {
			const propType = property.type;

			switch (propType) {
				case "number":
					if (Number.isNaN(Number(value))) {
						error = "Must be a valid number";
					}
					break;
				case "integer":
					if (!Number.isInteger(Number(value)) || Number.isNaN(Number(value))) {
						error = "Must be a valid integer";
					}
					break;
				case "boolean":
					if (value !== "true" && value !== "false") {
						error = "Must be true or false";
					}
					break;
				case "string":
					if (key.toLowerCase().includes("url")) {
						try {
							new URL(value);
						} catch (_e) {
							error = "Must be a valid URL";
						}
					} else if (key.toLowerCase().includes("email")) {
						const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
						if (!emailRegex.test(value)) {
							error = "Must be a valid email address";
						}
					}
					break;
			}
		}

		setCredentialErrors((prev) => ({
			...prev,
			[key]: error
		}));

		return error === "";
	};

	const renderCredentialFields = () => {
		if (!credentialSchema || !credentialSchema.properties) {
			return (
				<div className="p-4 text-center text-gray-500">
					<FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
					No credential schema available
				</div>
			);
		}

		return Object.entries(credentialSchema.properties).map(
			([key, property]) => {
				const inputType = getInputType(property);

				return (
					<div key={key} className="mb-4">
						<label
							htmlFor={key}
							className="block text-sm font-medium text-gray-700 mb-1"
						>
							{key.charAt(0).toUpperCase() + key.slice(1)}
							{isFieldRequired(key) && (
								<span className="text-red-500 ml-1">*</span>
							)}
						</label>
						<div className="relative">
							{inputType === "checkbox" ? (
								<input
									type="checkbox"
									id={key}
									name={key}
									checked={credentials[key] === "true"}
									onChange={(e) =>
										handleCredentialChange(
											key,
											e.target.checked ? "true" : "false"
										)
									}
									className="h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-300 rounded"
									disabled={!editMode || isLoading}
								/>
							) : (
								<input
									type={inputType}
									id={key}
									name={key}
									value={credentials[key] || ""}
									onChange={(e) => handleCredentialChange(key, e.target.value)}
									className={`w-full p-2 border ${
										credentialErrors[key] ? "border-red-500" : "border-gray-300"
									} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
										!editMode ? "bg-gray-100" : ""
									}`}
									placeholder={`Enter ${key}`}
									required={isFieldRequired(key)}
									disabled={!editMode || isLoading}
									readOnly={!editMode}
								/>
							)}
							{credentialErrors[key] && (
								<p className="text-red-500 text-xs mt-1">
									<FontAwesomeIcon
										icon={faExclamationCircle}
										className="mr-1"
									/>
									{credentialErrors[key]}
								</p>
							)}
						</div>
						{property.description && (
							<p className="mt-1 text-xs text-gray-500">
								{property.description}
							</p>
						)}
					</div>
				);
			}
		);
	};

	const handleToggleStatus = (flowDefinitionId: string) => {
		setFlowStatuses((prev) => ({
			...prev,
			[flowDefinitionId]: !prev[flowDefinitionId]
		}));
	};

	const renderFlowDefinitions = () => {
		if (!flows || flows.length === 0) {
			return (
				<div className="p-4 text-center text-gray-500">
					<FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
					No flow definitions available
				</div>
			);
		}

		const uniqueDefinitions = flows.reduce(
			(acc, flow) => {
				if (!acc[flow.flowDefinitionId]) {
					acc[flow.flowDefinitionId] = flow;
				}
				return acc;
			},
			{} as Record<string, CustomEditFlowPayload>
		);

		const flowsToRender = Object.values(uniqueDefinitions);

		return flowsToRender.map((flow) => {
			const hasActiveFlow = flow.flowId;

			const isEnabled =
				flowStatuses[flow.flowDefinitionId] !== undefined
					? flowStatuses[flow.flowDefinitionId]
					: !!hasActiveFlow;

			return (
				<div
					key={flow.flowDefinitionId}
					className={`border rounded-md p-4 transition-colors ${
						hasActiveFlow
							? isEnabled
								? "bg-green-50 border-green-200"
								: "bg-yellow-50 border-yellow-200"
							: isEnabled
								? "bg-green-50 border-green-200"
								: "bg-gray-50 border-gray-200"
					}`}
				>
					<div className="flex items-center justify-between">
						<div className="flex-grow">
							<div className="flex items-center">
								<h4 className="font-medium text-gray-800 mr-2">
									{flow.actionName}
								</h4>
							</div>
						</div>
						<div className="flex items-center space-x-2">
							<div className="flex items-center">
								<button
									onClick={() => handleToggleStatus(flow.flowDefinitionId)}
									className={`w-12 h-6 flex items-center rounded-full p-1 ${
										isEnabled ? "bg-green-500" : "bg-gray-300"
									} transition-colors duration-300 focus:outline-none`}
									aria-label={isEnabled ? "Disable flow" : "Enable flow"}
									type="button"
								>
									<span
										className={`bg-white w-4 h-4 rounded-full shadow-md transform ${
											isEnabled ? "translate-x-6" : "translate-x-0"
										} transition-transform duration-300`}
									/>
								</button>
							</div>
						</div>
					</div>
				</div>
			);
		});
	};

	const renderLogoPreview = (logo: string | null, flowLogo: string | null) => {
		if (!logo) {
			return (
				<div className="mt-4 border rounded-lg p-4 bg-gray-50">
					<div className="flex justify-center">
						<div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
							<FontAwesomeIcon
								icon={faBuilding}
								className="text-blue-500 text-2xl"
							/>
						</div>
					</div>
					<div className="text-center mt-2 text-gray-500 text-sm">
						No logo available
					</div>
				</div>
			);
		}

		try {
			let isValidImage = false;
			let customLogo = "";

			if (isValidBase64(flowLogo || "")) {
				customLogo = flowLogo || "";
				isValidImage = true;
			} else if (isValidBase64(logo)) {
				customLogo = logo;
				isValidImage = true;
			}

			if (!isValidImage) {
				return (
					<div className="mt-4 border rounded-lg p-4 bg-gray-50">
						<div className="flex justify-center">
							<div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
								<FontAwesomeIcon
									icon={faExclamationTriangle}
									className="text-red-500 text-2xl"
								/>
							</div>
						</div>
						<div className="text-center mt-2 text-gray-500 text-sm">
							Invalid logo format
						</div>
					</div>
				);
			}

			return (
				<div className="mt-4 border rounded-lg p-4 bg-gray-50">
					<div className="flex justify-center">
						<img
							src={customLogo}
							alt="Target Logo Preview"
							className="max-h-48 max-w-full object-contain"
							onError={(e) => {
								e.currentTarget.style.display = "none";
								const parent = e.currentTarget.parentNode;
								if (parent) {
									const fallbackEl = document.createElement("div");
									fallbackEl.className =
										"w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center";
									fallbackEl.innerHTML =
										'<i class="fas fa-building text-blue-500 text-2xl"></i>';
									parent.appendChild(fallbackEl);
								}
							}}
						/>
					</div>
				</div>
			);
		} catch (_error) {
			return (
				<div className="mt-4 border rounded-lg p-4 bg-gray-50">
					<div className="flex justify-center">
						<div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
							<FontAwesomeIcon
								icon={faBuilding}
								className="text-blue-500 text-2xl"
							/>
						</div>
					</div>
					<div className="text-center mt-2 text-gray-500 text-sm">
						Error loading logo
					</div>
				</div>
			);
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onloadend = () => {
			const base64String = reader.result as string;
			setLogoImage(base64String);
			setLogoDeleted(false);
		};
		reader.readAsDataURL(file);
	};

	const handleUpdate = () => {
		let hasErrors = false;

		if (showCredentialFields) {
			Object.entries(credentials).forEach(([key, value]) => {
				if (!validateCredentialValue(key, value)) {
					hasErrors = true;
				}
			});
		}

		if (hasErrors) {
			return;
		}

		onUpdateFlow?.({
			credential: showCredentialFields ? credentials : null,
			flows: flows
				.filter((flow) => flow.flowId)
				.map((flow) => ({
					id: flow.flowId as string,
					status:
						flowStatuses[flow.flowDefinitionId] !== undefined
							? flowStatuses[flow.flowDefinitionId]
								? "ENABLED"
								: "DISABLED"
							: flow.flowStatus === "ENABLED"
								? "ENABLED"
								: "DISABLED"
				})),
			flowDefinitions: flows
				.filter((flow) => !flow.flowId && flowStatuses[flow.flowDefinitionId])
				.map((flow) => ({ id: flow.flowDefinitionId })),
			logo:
				logoImage && isValidBase64(logoImage)
					? logoImage
					: logoDeleted
						? "logo"
						: flows.find((flow) => flow.logo && isValidBase64(flow.logo))
								?.logo || "logo"
		});

		setEditMode(false);
	};

	const handleDeleteAllFlows = () => {
		setShowDeleteConfirm(true);
	};

	const confirmDeleteAllFlows = () => {
		flows.forEach((flow) => {
			if (flow.flowId) {
				onDeleteFlow(flow.flowId);
			}
		});
		toast.success("Flow deleted successfully");
		setShowDeleteConfirm(false);
	};

	const cancelDeleteFlow = () => {
		setShowDeleteConfirm(false);
	};

	return (
		<div ref={panelRef} className="p-2">
			{/* Flow Definitions Section */}
			<div className="mb-8 bg-white border border-gray-200 rounded-lg shadow-sm">
				<div className="p-4 border-b border-gray-200">
					<h3 className="font-medium text-lg flex items-center">
						<FontAwesomeIcon icon={faStream} className="mr-2 text-blue-500" />
						Actions
					</h3>
				</div>

				<div className="p-4">
					<div className="space-y-4">{renderFlowDefinitions()}</div>
				</div>
			</div>

			{/* Credentials Section */}
			<div className="mb-8 bg-white border border-gray-200 rounded-lg shadow-sm">
				<div className="p-4 border-b border-gray-200 flex items-center justify-between">
					<h3 className="font-medium text-lg flex items-center">
						<FontAwesomeIcon
							icon={faKey || faPuzzlePiece}
							className="mr-2 text-blue-500"
						/>
						Credentials
						<span className="ml-2 text-sm font-normal text-gray-500">
							{!showCredentialWarning ? "(Available)" : "(Warning)"}
						</span>
					</h3>
					{!showCredentialWarning && !showCredentialFields && (
						<button
							type="button"
							onClick={() => setShowCredentialWarning(true)}
							className="px-4 py-2 rounded-md text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center"
						>
							<FontAwesomeIcon icon={faEdit} className="mr-2" />
							Edit
						</button>
					)}
				</div>

				<div className="p-4">
					{!showCredentialWarning && !showCredentialFields ? (
						<div className="text-gray-600">
							{hasExistingCredentials
								? 'Existing credentials will be used. Click "Edit" to modify them.'
								: 'No credentials configured yet. Click "Edit" to add them.'}
						</div>
					) : showCredentialWarning ? (
						<div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
							<div className="flex">
								<div className="flex-shrink-0">
									<FontAwesomeIcon
										icon={faExclamationTriangle}
										className="h-5 w-5 text-yellow-400"
									/>
								</div>
								<div className="ml-3">
									<h3 className="text-sm font-medium text-yellow-800">
										Warning: Credential Update
									</h3>
									<div className="mt-2 text-sm text-yellow-700">
										<p>
											Editing credentials will permanently delete the old
											credentials from the database and replace them with the
											new ones. This action cannot be undone.
										</p>
									</div>
									<div className="mt-4">
										<div className="flex space-x-3">
											<button
												type="button"
												onClick={() => {
													setShowCredentialWarning(false);
													setShowCredentialFields(true);
												}}
												className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
											>
												Continue Editing
											</button>
											<button
												type="button"
												onClick={() => {
													setShowCredentialWarning(false);
												}}
												className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
											>
												Cancel
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					) : (
						renderCredentialFields()
					)}
				</div>
			</div>

			{/* Logo Upload */}
			<div className="mb-8 bg-white border border-gray-200 rounded-lg shadow-sm">
				<div className="p-4 border-b border-gray-200">
					<h3 className="font-medium text-lg flex items-center">
						<FontAwesomeIcon icon={faImage} className="mr-2 text-blue-500" />
						Logo Image
					</h3>
				</div>

				<div className="p-4">
					<div className="mt-1 space-y-4">
						<div className="flex items-center justify-between space-x-2">
							<label className="w-full relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-700 focus-within:outline-none">
								<span className="w-full inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
									{logoImage ? "Change Logo" : "Upload Logo"}
								</span>
								<input
									type="file"
									className="sr-only"
									accept="image/*"
									onChange={handleFileChange}
									disabled={isLoading}
								/>
							</label>
							{logoImage && (
								<button
									type="button"
									onClick={() => {
										setLogoImage(null);
										setLogoDeleted(true);
									}}
									className="p-1 bg-gray-100 rounded-full hover:bg-gray-200 focus:outline-none"
								>
									<FontAwesomeIcon icon={faTrash} className="text-red-500" />
								</button>
							)}
						</div>

						{renderLogoPreview(logoImage, flows[0]?.logo)}
					</div>
				</div>
			</div>

			{/* Bottom Action Buttons */}
			<div className="flex w-full mt-8 gap-x-2">
				<button
					type="button"
					onClick={handleUpdate}
					className="w-1/2 px-4 py-2 rounded-md text-sm font-medium bg-green-500 text-white hover:bg-green-600 transition-colors"
					disabled={isLoading}
				>
					<FontAwesomeIcon icon={faCheck} className="mr-2" />
					Save
				</button>
				<button
					type="button"
					onClick={handleDeleteAllFlows}
					className="w-1/2 px-4 py-2 rounded-md text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
					disabled={isLoading}
				>
					<FontAwesomeIcon icon={faTrash} className="mr-2" />
					Delete
				</button>
			</div>

			{/* Delete Confirmation Modal */}
			{showDeleteConfirm && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-4 max-w-md w-full">
						<div className="text-center">
							<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
								<FontAwesomeIcon
									icon={faExclamationTriangle}
									className="h-6 w-6 text-red-600"
								/>
							</div>
							<h3 className="mt-3 text-lg leading-6 font-medium text-gray-900">
								Delete Flows
							</h3>
							<div className="mt-2 px-2">
								<p className="text-sm text-gray-500">
									Are you sure you want to delete all flow definitions? This
									action cannot be undone and will remove all associated flows.
								</p>
							</div>
						</div>
						<div className="mt-5 flex justify-center space-x-3">
							<button
								type="button"
								onClick={cancelDeleteFlow}
								className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={confirmDeleteAllFlows}
								className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none"
							>
								Delete
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default EditFlowPanel;
